import { useState, useMemo } from 'react';
import { Card, Table, Alert } from '@nimbus-ds/components';
import { WeekdaysType } from '@tiendanube/common';
import { useResponsive } from 'commons/components';
import useTranslationShipping from 'domains/Shipping/useTranslationShipping';
import BusinessHourItem from './BusinessHourItem';
import PickupBusinessHoursSkeleton from './Skeleton';
import {
  END_DEFAULT,
  ExtendedBusinessHoursResponseDto,
  formatBusinessHourErrors,
  sortBusinessHours,
  START_DEFAULT,
  WEEKDAYS_OPTIONS,
  getBusinessHoursWithIndex,
} from './utils';
import './PickupBusinessHours.scss';

interface PickupBusinessHoursProps {
  businessHours: ExtendedBusinessHoursResponseDto[];
  errors: Partial<Record<string, string>>;
  setFieldValue: (name: string, value: any) => void;
}

function PickupBusinessHours({
  businessHours,
  errors,
  setFieldValue,
}: Readonly<PickupBusinessHoursProps>): JSX.Element {
  const t = useTranslationShipping(
    'deliveryMethods.pickupPoints.form.secondStep.businessHours',
  );
  const { isMobile } = useResponsive();

  const businessHoursWithIndex = useMemo(
    () => getBusinessHoursWithIndex(businessHours),
    [businessHours],
  );

  const formattedErrors = formatBusinessHourErrors(
    errors,
    businessHoursWithIndex,
  );
  const [dayChanged, setDayChanged] = useState<string | null>(
    businessHoursWithIndex.length > 0
      ? businessHoursWithIndex[0].day
      : 'monday',
  );

  const businessHoursByDay = WEEKDAYS_OPTIONS.reduce((acc, day) => {
    acc[day] = businessHoursWithIndex.filter((bh) => bh.day === day);
    return acc;
  }, {} as Record<WeekdaysType, ExtendedBusinessHoursResponseDto[]>);

  const handleChangeHour = (
    newBusinessHour: ExtendedBusinessHoursResponseDto,
  ) => {
    setDayChanged(newBusinessHour.day);
    const newBusinessHours = businessHoursWithIndex.filter(
      (businessHour) =>
        !(
          businessHour.day === newBusinessHour.day &&
          businessHour.index === newBusinessHour.index
        ),
    );

    newBusinessHours.push(newBusinessHour);
    setFieldValue('businessHours', sortBusinessHours(newBusinessHours));
  };

  const handleChangeDay = (day: WeekdaysType, active: boolean) => {
    if (!active) {
      const newBusinessHours = businessHoursWithIndex.filter(
        (businessHour) => businessHour.day !== day,
      );
      setFieldValue('businessHours', sortBusinessHours(newBusinessHours));
      return;
    }

    const newBusinessHour = {
      day,
      start: START_DEFAULT,
      end: END_DEFAULT,
      index: businessHoursByDay[day].length,
    };

    setFieldValue(
      'businessHours',
      sortBusinessHours([...businessHoursWithIndex, newBusinessHour]),
    );
  };

  const handleApplyAll = (
    newBusinessHour: ExtendedBusinessHoursResponseDto,
  ) => {
    const sourceDay = newBusinessHour.day;
    const sourceDayBusinessHours = businessHoursByDay[sourceDay];

    if (sourceDayBusinessHours.length === 0) {
      return;
    }

    const activeDays = WEEKDAYS_OPTIONS.filter(
      (day) => businessHoursByDay[day as WeekdaysType].length > 0,
    );

    const newBusinessHours = WEEKDAYS_OPTIONS.flatMap((targetDay) => {
      if (targetDay === sourceDay) {
        return sourceDayBusinessHours;
      }

      if (activeDays.includes(targetDay)) {
        return sourceDayBusinessHours.map((bh, idx) => ({
          ...bh,
          day: targetDay as WeekdaysType,
          index: idx,
        }));
      }

      return [];
    }) as ExtendedBusinessHoursResponseDto[];

    setFieldValue('businessHours', sortBusinessHours(newBusinessHours));
  };

  const handleAddBusinessHour = (day: WeekdaysType) => {
    const dayBusinessHours = businessHoursByDay[day];
    const newBusinessHour = {
      day,
      start: START_DEFAULT,
      end: END_DEFAULT,
      index: dayBusinessHours.length,
    };

    const otherDaysBusinessHours = businessHoursWithIndex.filter(
      (bh) => bh.day !== day,
    );

    const updatedDayBusinessHours = [...dayBusinessHours, newBusinessHour];

    const newBusinessHours = [
      ...otherDaysBusinessHours,
      ...updatedDayBusinessHours,
    ];
    setFieldValue('businessHours', sortBusinessHours(newBusinessHours));
  };

  const handleRemoveBusinessHour = (day: WeekdaysType) => {
    const dayBusinessHours = businessHoursByDay[day];

    if (dayBusinessHours.length <= 1) {
      const newBusinessHours = businessHoursWithIndex.filter(
        (bh) => bh.day !== day,
      );
      setFieldValue('businessHours', sortBusinessHours(newBusinessHours));
      return;
    }

    const lastBusinessHour = dayBusinessHours[dayBusinessHours.length - 1];

    const newBusinessHours = businessHoursWithIndex.filter(
      (bh) => !(bh.day === day && bh.index === lastBusinessHour.index),
    );

    setFieldValue('businessHours', sortBusinessHours(newBusinessHours));
  };

  return (
    <Card padding="none">
      <Card.Header padding="base" title={t('title')} />
      <Card.Body>
        <div className="stratus--custom-table">
          <Table>
            <Table.Head>
              <Table.Cell backgroundColor="neutral-surface" padding="base">
                {t('day')}
              </Table.Cell>
              <Table.Cell backgroundColor="neutral-surface" padding="base">
                {t('start')}
              </Table.Cell>
              <Table.Cell backgroundColor="neutral-surface" padding="base">
                {t('end')}
              </Table.Cell>
              <Table.Cell
                backgroundColor="neutral-surface"
                padding="base"
                width={isMobile ? '3vw' : ''}
              >
                {isMobile ? '' : t('action')}
              </Table.Cell>
            </Table.Head>
            <Table.Body>
              {WEEKDAYS_OPTIONS.map((weekday) => {
                const dayBusinessHours =
                  businessHoursByDay[weekday as WeekdaysType];
                const isActive = dayBusinessHours.length > 0;

                return (
                  <BusinessHourItem
                    key={weekday}
                    day={weekday as WeekdaysType}
                    isDayChanged={dayChanged === weekday}
                    businessHours={dayBusinessHours}
                    errors={formattedErrors}
                    changeHour={handleChangeHour}
                    handleApplyAll={handleApplyAll}
                    handleChangeDay={handleChangeDay}
                    handleAddBusinessHour={handleAddBusinessHour}
                    handleRemoveBusinessHour={handleRemoveBusinessHour}
                    isActive={isActive}
                  />
                );
              })}
            </Table.Body>
          </Table>
        </div>
      </Card.Body>
      {!!formattedErrors.businessHours && (
        <Card.Footer padding="base">
          <Alert appearance="danger" title={t('errors.businessHoursRequired')}>
            {' '}
          </Alert>
        </Card.Footer>
      )}
    </Card>
  );
}

PickupBusinessHours.Skeleton = PickupBusinessHoursSkeleton;
export default PickupBusinessHours;
